import React, { createContext, useContext, useMemo, useState } from "react";

const AuthContext = createContext(undefined);
const STORAGE_KEY = "ainsoft_admin_session";

function readStoredSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { user: null, token: null };
    const stored = JSON.parse(raw);
    if (stored.expiresAt && new Date(stored.expiresAt) < new Date()) {
      localStorage.removeItem(STORAGE_KEY);
      return { user: null, token: null };
    }
    return {
      user: stored.user ?? null,
      token: stored.token ?? null
    };
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return { user: null, token: null };
  }
}

export const AuthProvider = ({ children }) => {
  const [{ user, token }, setSession] = useState(readStoredSession);

  const login = (nextUser, nextToken, expiresAt) => {
    const next = { user: nextUser, token: nextToken, expiresAt: expiresAt ?? null };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setSession(next);
  };

  const updateUser = (patch) => {
    setSession((prev) => {
      if (!prev.user) return prev;
      const next = {
        ...prev,
        user: { ...prev.user, ...patch }
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSession({ user: null, token: null });
  };

  const value = useMemo(() => ({ user, token, login, logout, updateUser }), [user, token]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
};
